import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useCallback,
} from 'react';
import { User, LoginCredentials, AuthContextType, UserRole } from '../types/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Session timeout configuration (30 minutes of inactivity)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const SESSION_WARNING_MS = 5 * 60 * 1000; // Show warning 5 minutes before timeout

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Load user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('tacolcy_token');
    const storedUser = localStorage.getItem('tacolcy_user');

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        lastActivityRef.current = Date.now();
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('tacolcy_token');
        localStorage.removeItem('tacolcy_user');
      }
    }
    setIsLoading(false);
  }, []);

  // Clear timers
  const clearTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    setShowTimeoutWarning(false);
  }, []);

  // Auto logout handler
  const handleAutoLogout = useCallback(() => {
    clearTimers();
    setUser(null);
    setToken(null);
    localStorage.removeItem('tacolcy_token');
    localStorage.removeItem('tacolcy_user');
    // Use a custom event to allow the app to handle navigation
    window.dispatchEvent(new CustomEvent('auth:timeout'));
  }, [clearTimers]);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    clearTimers();

    if (!user || !token) {
      return;
    }

    lastActivityRef.current = Date.now();

    // Set warning timer (5 minutes before timeout)
    warningTimerRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
    }, SESSION_TIMEOUT_MS - SESSION_WARNING_MS);

    // Set logout timer (30 minutes of inactivity)
    inactivityTimerRef.current = setTimeout(() => {
      handleAutoLogout();
    }, SESSION_TIMEOUT_MS);
  }, [user, token, clearTimers, handleAutoLogout]);

  // Track user activity (mouse movements, clicks, key presses, scrolls)
  useEffect(() => {
    if (!user || !token) {
      clearTimers();
      return;
    }

    // Initialize timer on mount
    resetInactivityTimer();

    // Event listeners for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    const activityHandler = () => {
      lastActivityRef.current = Date.now();
      if (showTimeoutWarning) {
        setShowTimeoutWarning(false);
      }
      resetInactivityTimer();
    };

    events.forEach((event) => {
      document.addEventListener(event, activityHandler, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, activityHandler, true);
      });
      clearTimers();
    };
  }, [user, token, showTimeoutWarning, resetInactivityTimer, clearTimers]);

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Login failed');
      }

      const data = await response.json();

      if (data.success && data.data?.user && data.data?.token) {
        setUser(data.data.user);
        setToken(data.data.token);
        localStorage.setItem('tacolcy_token', data.data.token);
        localStorage.setItem('tacolcy_user', JSON.stringify(data.data.user));
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearTimers();
    setUser(null);
    setToken(null);
    localStorage.removeItem('tacolcy_token');
    localStorage.removeItem('tacolcy_user');
  };

  // Extend session (called when user dismisses warning)
  const extendSession = useCallback(() => {
    setShowTimeoutWarning(false);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const hasRole = (roles: UserRole[]): boolean => {
    if (!user) {
      return false;
    }
    return roles.includes(user.role);
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
    hasRole,
    showTimeoutWarning,
    extendSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Session Timeout Warning Modal */}
      {showTimeoutWarning && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '500px',
              margin: '1rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            <h2 style={{ marginTop: 0, color: '#D32F2F' }}>Session Timeout Warning</h2>
            <p>Your session will expire in 5 minutes due to inactivity.</p>
            <p>Click &quot;Stay Logged In&quot; to continue your session.</p>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                onClick={extendSession}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#5B2C86',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Stay Logged In
              </button>
              <button
                onClick={logout}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#9D9FA1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
